import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { H3, P } from "~/ui-rtk/components/ui/typography"
import Table, {
  TCellItem,
  THandleAction,
} from "~/ui-rtk/components/ui/common/Table"
import { Card, Chip, Dialog } from "~/ui-rtk/components/ui/common"
import { Button, Input, Toggle } from "~/ui-rtk/components/ui/controls"
import { DataSaverSvg, TrashSvg } from "~/ui-rtk/components/ui/svg/essentials"
import {
  useCliControllerListApiKeysQuery,
  useCliControllerCreateApiKeyMutation,
  useCliControllerRevokeApiKeyMutation,
} from "~/ui-rtk/api/cliApi"
import { copyTextToClipboard } from "~/ui-rtk/utils/clipboard"

import useDate from "~/ui-rtk/hooks/date"
const dayjs = useDate()

const REVOKE_ACTION = "REVOKE"

const renderStatusChipCell = (item: TCellItem) => {
  const isRevoked = !!item.revokedAt
  const color = isRevoked ? "text-basic-grey-inactive" : "text-states-success"
  const text = isRevoked ? "Revoked" : "Active"
  return (
    <Chip
      className={`bg-basic-blue text-3 leading-4 w-16 rounded-1 px-3 py-1.5 justify-center ${color}`}
    >
      {text}
    </Chip>
  )
}

const renderAccessChipCell = (item: TCellItem) => {
  const isReadOnly = !!item.readOnly
  const color = isReadOnly ? "text-basic-grey-inactive" : "text-states-warning"
  const text = isReadOnly ? "Read-only" : "Read-write"
  return (
    <Chip
      className={`bg-basic-blue text-3 leading-4 w-24 rounded-1 px-3 py-1.5 justify-center ${color}`}
    >
      {text}
    </Chip>
  )
}

const renderDateCell = (value: string) => (item: TCellItem) => {
  const dateStr = item[value]
  const date =
    !dateStr || dateStr === "Never"
      ? "Never"
      : dayjs(dateStr).format("MM/DD/YYYY")
  return <p>{date}</p>
}

const renderActionCell = (item: TCellItem, handleAction: THandleAction) => {
  const size = 18
  const isRevoked = !!item.revokedAt

  if (isRevoked) {
    return <></>
  }

  const handleRevoke = () => {
    void handleAction(REVOKE_ACTION, item)
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        onClick={handleRevoke}
        variant={{ variant: "text" }}
        className="p-0"
      >
        <TrashSvg size={size} />
      </Button>
    </div>
  )
}

export default function SettingsApiKeysPage() {
  const { data: apiKeys = [], isLoading } = useCliControllerListApiKeysQuery()
  const [createApiKey, { isLoading: isCreating }] =
    useCliControllerCreateApiKeyMutation()
  const [revokeApiKey] = useCliControllerRevokeApiKeyMutation()

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isTokenDialogOpen, setIsTokenDialogOpen] = useState(false)
  const [newToken, setNewToken] = useState("")
  const [label, setLabel] = useState("")
  const [readOnly, setReadOnly] = useState(true)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    document.title = "Marathon Data: Settings > API Keys"
  }, [])

  const handleCreate = async () => {
    try {
      const result = await createApiKey({
        label: label || undefined,
        readOnly,
      }).unwrap()
      setNewToken(result.token)
      setIsCreateDialogOpen(false)
      setIsTokenDialogOpen(true)
      setLabel("")
      toast.success("API key created")
    } catch {
      toast.error("Failed to create API key")
    }
  }

  const handleRevoke = async (id: string) => {
    try {
      await revokeApiKey(id).unwrap()
      toast.success("API key revoked")
    } catch {
      toast.error("Failed to revoke API key")
    }
  }

  const handleAction = (actionId: string, item: TCellItem) => {
    if (actionId === REVOKE_ACTION) {
      void handleRevoke(item.id as string)
    }
  }

  const handleCopy = () => {
    void copyTextToClipboard(newToken)
      .then(() => {
        setCopied(true)
        toast.success("Copied to clipboard")
        setTimeout(() => setCopied(false), 3000)
      })
      .catch(() => {
        toast.error("Failed to copy — please select and copy the key manually")
      })
  }

  const handleCloseTokenDialog = () => {
    setIsTokenDialogOpen(false)
    setNewToken("")
    setCopied(false)
  }

  const handleOpenCreateDialog = () => {
    setLabel("")
    setReadOnly(true)
    setIsCreateDialogOpen(true)
  }

  const activeKeys = apiKeys.filter(k => !k.revokedAt)
  const revokedKeys = apiKeys.filter(k => k.revokedAt)

  const tableItems = [
    ...activeKeys.map(k => ({
      id: k.id,
      label: k.label || "Untitled",
      readOnly: k.readOnly,
      createdAt: k.createdAt,
      lastUsedAt: k.lastUsedAt,
      revokedAt: k.revokedAt,
    })),
    ...revokedKeys.map(k => ({
      id: k.id,
      label: k.label || "Untitled",
      readOnly: k.readOnly,
      createdAt: k.createdAt,
      lastUsedAt: k.lastUsedAt,
      revokedAt: k.revokedAt,
    })),
  ]

  let content
  if (isLoading) {
    content = (
      <div className="mx-auto">
        <DataSaverSvg className="animate-spin" />
      </div>
    )
  } else if (tableItems.length === 0) {
    content = (
      <div className="flex flex-col items-center justify-center py-16 text-basic-grey-inactive">
        <P>No API keys yet. Create one to get started.</P>
      </div>
    )
  } else {
    content = (
      <Table
        headers={[
          {
            label: "Label",
            value: "label",
          },
          {
            label: "Created",
            customRender: renderDateCell("createdAt"),
          },
          {
            label: "Last Used",
            customRender: renderDateCell("lastUsedAt"),
          },
          {
            label: "Access",
            customRender: renderAccessChipCell,
          },
          {
            label: "Status",
            customRender: renderStatusChipCell,
          },
          {
            label: "Actions",
            customRender: (item: TCellItem, handleAction?: THandleAction) =>
              renderActionCell(item, handleAction ?? (() => null)),
            cellClassName: "relative",
          },
        ]}
        items={tableItems}
        handleAction={handleAction}
        classes={{
          thead: "",
          tbody: "",
          th: "text-3.5",
        }}
      />
    )
  }

  const createDialog = (
    <Dialog
      closeOnClickOutside={true}
      isOpen={isCreateDialogOpen}
      handleClose={() => setIsCreateDialogOpen(false)}
      width={500}
      height="auto"
    >
      <Card
        className="w-full h-full border bg-basic-black border-basic-blue"
        rounded
      >
        <div className="flex flex-col gap-4">
          <H3>Create API Key</H3>
          <P className="text-platinum">
            Give your key an optional label to identify it later.
          </P>
          <div>
            <Input
              fullWidth
              placeholder="e.g. CI/CD Pipeline"
              value={label}
              onChange={e => setLabel(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  void handleCreate()
                }
              }}
            />
          </div>
          <div className="flex items-center gap-3">
            <Toggle size="sm" value={readOnly} onChange={setReadOnly} />
            <div>
              <P>Read-only</P>
              <P className="text-3 text-basic-grey-inactive">
                Read-only keys cannot modify campaigns, budgets, or other data.
              </P>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant={{ variant: "outlined" }}
              size="sm"
              onClick={() => setIsCreateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant={{ variant: "solid", color: "pink" }}
              size="sm"
              onClick={() => void handleCreate()}
              isLoading={isCreating}
            >
              Create
            </Button>
          </div>
        </div>
      </Card>
    </Dialog>
  )

  const tokenDialog = (
    <Dialog
      closeOnClickOutside={false}
      isOpen={isTokenDialogOpen}
      handleClose={handleCloseTokenDialog}
      width={600}
      height="auto"
    >
      <Card
        className="w-full h-full border bg-basic-black border-basic-blue"
        rounded
      >
        <div className="flex flex-col gap-4">
          <H3>Your API Key</H3>
          <div className="p-3 rounded-lg border border-states-warning bg-states-warning/10">
            <P className="text-states-warning font-semibold">
              Copy this key now. You won't be able to see it again.
            </P>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 p-3 text-sm break-all rounded-lg border border-basic-blue bg-basic-dark-blue font-mono">
              {newToken}
            </code>
            <Button
              variant={{ variant: "solid", color: "white" }}
              size="sm"
              onClick={handleCopy}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <div className="flex justify-end">
            <Button
              variant={{ variant: "solid", color: "pink" }}
              size="sm"
              onClick={handleCloseTokenDialog}
            >
              Done
            </Button>
          </div>
        </div>
      </Card>
    </Dialog>
  )

  return (
    <div className="py-6">
      <H3>API Keys</H3>
      <P className="my-3">
        API keys allow CLI tools and scripts to access the Marathon Data API on
        your behalf.
      </P>
      <div className="flex justify-end mt-2 mb-10">
        <Button
          variant={{ variant: "solid", color: "pink" }}
          size="sm"
          onClick={handleOpenCreateDialog}
        >
          Create API Key
        </Button>
      </div>
      {content}
      {createDialog}
      {tokenDialog}
    </div>
  )
}
